import { ReactElement, useState } from "react"

import { Blockquote, FormLabel, Loading, TextInput } from "@wfp/ui"

import { useSaveXlsFormMutation, useUploadXlsFormFileMutation } from "services/marketprices/xlsFormSchemas"
import FileUploader from "../../Shared/ExcelUploader/FileUploader"

import { useDataModaUploadContext } from "../../../providers/DataModaUploadContext"
import { FieldsData } from "../../../interfaces/mappingInterfaces"
import XlsFormFieldMapping from "../../XlsForm/XlsFormFieldMapping"

import Button from "../../Shared/Button/Button"

const UploadXlsFile = (): ReactElement => {
  const {
    xlsFile,
    setXlsFile,
    xlsFormName,
    setXlsFormName,
    handleSaveXlsFile,
    country,
    setXlsFormId,
    handleSaveMappingSchema,
  } = useDataModaUploadContext()

  const [fieldsData, setFieldsData] = useState<FieldsData>({
    questionLabelPosition: 0,
    choiceLabelPosition: 0,
  })
  const [errorSaveXls, setErrorSaveXls] = useState<string | null>(null)

  const [saveXls, { data: fileHeaders, isLoading: isSaveXlsLoading }] = useUploadXlsFormFileMutation()
  const [saveXlsForm, { isLoading, isSuccess }] = useSaveXlsFormMutation()

  const handleSaveXls = async (file: File) => {
    try {
      const formData = new FormData()
      formData.append("file", file)
      await saveXls(formData).unwrap()
    } catch (error) {
      const errorMessage = error as { data: Record<string, string> }
      setErrorSaveXls(errorMessage?.data?.resultMessage || "Error saving XLS file")
    }
  }

  const handleCreate = async () => {
    if (!xlsFile || !xlsFormName) {
      console.error("XLS file or form name is missing")
      return
    }

    try {
      const response = await saveXlsForm({
        baseXlsFormSchemaId: 0,
        adm0code: country as number,
        name: xlsFormName,
        // xlsFormTypeId: 4,
        file: xlsFile,
        questionLabelPosition: fieldsData.questionLabelPosition,
        choiceLabelPosition: fieldsData.choiceLabelPosition,
      }).unwrap()
      setXlsFormId(response.xlsFormId)
      handleSaveMappingSchema(response.xlsFormId)
    } catch (error) {
      const errorMessage = error as { data: Record<string, string> }
      setErrorSaveXls(errorMessage?.data?.resultMessage || "Error saving XLS file")
    }
  }

  const onUploadFile = files => {
    setErrorSaveXls(null)
    setFieldsData({
      questionLabelPosition: 0,
      choiceLabelPosition: 0,
    })
    handleSaveXlsFile().then(() => {
      handleSaveXls(files)
    })
  }

  if (isSaveXlsLoading || isLoading) {
    return (
      <div className="w-full h-[300px] flex justify-center items-center">
        <Loading active={isLoading} small withOverlay={false} />
      </div>
    )
  }

  return (
    <div>
      <div className="row">
        <div className="text-xl font-bold">Upload the XLSForm</div>
        <p className="text-sm text-gray-500">Upload the XLSForm used for price data collection.</p>
      </div>

      <div className="row mt-2 xl:w-1/2 w-full">
        <FormLabel className="required">Form Name</FormLabel>
        <TextInput
          id="form-label"
          placeholder="Enter a name for this mapping"
          value={xlsFormName || ""}
          onChange={e => {
            setXlsFormName(e.target.value)
          }}
          className="custom-text-field"
        />
      </div>

      <div className="mt-2 xl:w-1/2 w-full">
        <FormLabel className="required">XLSForm</FormLabel>
        {!xlsFormName ? (
          <div className="mt-2 p-4 border border-gray-300 border-dashed rounded-lg text-center text-sm text-gray-500">
            Please enter a form name before uploading the XLSForm.
          </div>
        ) : (
          <FileUploader
            onChange={files => {
              if (files && files[0]) {
                setXlsFile(files[0])
                onUploadFile(files[0])
              }
            }}
            files={xlsFile ? [xlsFile] : []}
            placeholder="Drag & drop or click to upload"
          />
        )}
      </div>
      <div className="mt-2 xl:w-1/2 w-full">
        {fileHeaders && (
          <XlsFormFieldMapping
            surveyList={fileHeaders.surveyHeaders}
            choiceList={fileHeaders.choicesHeaders}
            values={fieldsData}
            setValues={setFieldsData}
          />
        )}
      </div>
      <div className="mt-2 xl:w-1/2 w-full">
        {fileHeaders && errorSaveXls && (
          <Blockquote kind="error" withIcon className="mt-2">
            <p className="py-1">{errorSaveXls}</p>
          </Blockquote>
        )}

        {fileHeaders && !isLoading && (
          <div className="d-flex">
            {!errorSaveXls && !isSuccess && (
              <Button action={handleCreate} label="Save" className="btn-full blue mr-1" />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default UploadXlsFile
